import { apollo } from './graphql';
import { SCHEDULE_IMPORT, POLL_IMPORT } from './queries/import';
import { wait } from '../util/util';
import { t } from '../locales/translate';

export type ImportStatus = 'pending' | 'error' | 'done';

interface Import {
  id: string;
  status: ImportStatus;
  modifications: JSON | null;
}

function checkImportStatus(importObject: { status: Import['status'] }): boolean {
  switch (importObject.status) {
    case 'pending':
      return false;
    case 'done':
      return true;
    case 'error':
      throw new Error(t.imports.error);
  }
}

async function pollImport(importObject: Import): Promise<Import> {
  if (checkImportStatus(importObject)) {
    return importObject;
  }
  const result = await apollo.query({
    query: POLL_IMPORT,
    variables: { id: importObject.id },
    fetchPolicy: 'network-only',
  });
  const updatedImport = result.data?.import as Import;
  if (updatedImport && checkImportStatus(updatedImport)) {
    return updatedImport;
  }
  await wait(1);
  return await pollImport(updatedImport);
}

interface TicketCodeUsageImport {
  type: 'ticket_code_usage';
  file: File;
  params: {
    eventId: string;
  };
}

interface ExternalValidationCodeImport {
  type: 'external_validation_code';
  file: File;
  params: {
    eventId: string;
  };
}

interface ChartOverridesImport {
  type: 'chart_overrides';
  file: File;
  params: {
    productId: string;
  };
}

interface VoucherCodesImport {
  type: 'voucher_code';
  file: File;
  params: {
    voucherCampaignId: string;
  };
}

type ImportDefinition =
  | TicketCodeUsageImport
  | ExternalValidationCodeImport
  | ChartOverridesImport
  | VoucherCodesImport;

export async function runImport(definition: ImportDefinition): Promise<Import> {
  const result = await apollo.mutate({
    mutation: SCHEDULE_IMPORT,
    variables: definition,
  });
  const importObject: Import = result.data && result.data[Object.keys(result.data)[0]].import;
  await wait(1);
  return await pollImport(importObject);
}

export async function runTicketCodeUsageImport(eventId: string, file: File): Promise<JSON | null> {
  return (await runImport({ type: 'ticket_code_usage', file, params: { eventId } })).modifications;
}

export async function runExternalCodesImport(eventId: string, file: File): Promise<JSON | null> {
  return (await runImport({ type: 'external_validation_code', file, params: { eventId } })).modifications;
}

export async function runMusicChartOverridesImport(productId: string, file: File): Promise<JSON | null> {
  return (await runImport({ type: 'chart_overrides', file, params: { productId } })).modifications;
}

export async function runVoucherCodesImport(voucherCampaignId: string, file: File): Promise<void> {
  await runImport({ type: 'voucher_code', file, params: { voucherCampaignId } });
}
